import dayjs from 'dayjs';
import type { FieldConfig } from './types';

const STORAGE_PREFIX = 'caligula-admin:forge-query-remember:v1';

function isRememberEnabled(field: FieldConfig): boolean {
  return Boolean(field.rememberKey && field.rememberKey.trim());
}

function buildStorageKey(rememberKey: string, fieldKey: string): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(rememberKey)}:${fieldKey}`;
}

function isEmptyFieldValue(field: FieldConfig, value: any): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value)) {
    if (field.type === 'date') {
      return value.length !== 2 || !value[0] || !value[1];
    }
    return value.length === 0;
  }
  return false;
}

function serializeFieldValue(field: FieldConfig, value: any): any {
  if (field.type === 'date' && Array.isArray(value) && value.length === 2) {
    return value.map((v) => (dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v));
  }
  return value;
}

function deserializeFieldValue(field: FieldConfig, stored: any): any {
  if (stored === undefined || stored === null) return stored;
  if (field.type === 'date' && Array.isArray(stored) && stored.length === 2) {
    return stored.map((v) => dayjs(v));
  }
  return stored;
}

export function loadRememberedValues(fields: FieldConfig[]): Record<string, any> {
  const remembered: Record<string, any> = {};

  fields.forEach((field) => {
    if (!isRememberEnabled(field)) return;
    const rememberKey = field.rememberKey!.trim();

    const storageKey = buildStorageKey(rememberKey, field.key);
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const value = deserializeFieldValue(field, parsed);
      if (isEmptyFieldValue(field, value)) {
        localStorage.removeItem(storageKey);
        return;
      }
      remembered[field.key] = value;
    } catch {
      // ignore
    }
  });

  return remembered;
}

export function saveRememberedValues(fields: FieldConfig[], values: Record<string, any>): void {
  fields.forEach((field) => {
    if (!isRememberEnabled(field)) return;
    const rememberKey = field.rememberKey!.trim();

    const storageKey = buildStorageKey(rememberKey, field.key);
    const currentValue = values[field.key];

    if (isEmptyFieldValue(field, currentValue)) {
      localStorage.removeItem(storageKey);
      return;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(serializeFieldValue(field, currentValue)));
    } catch {
      // ignore
    }
  });
}

export function clearRememberedValues(fields: FieldConfig[]): void {
  fields.forEach((field) => {
    if (!isRememberEnabled(field)) return;
    const rememberKey = field.rememberKey!.trim();
    const storageKey = buildStorageKey(rememberKey, field.key);
    localStorage.removeItem(storageKey);
  });
}
